import { useMutation, useQueryClient } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import type { Task, TaskPatch } from "types/Task";
import { applyServerTaskRow } from "types/Task";
import { patchTask } from "../methods/patchTask";
import { TASKS_QUERY_KEY } from "./useTasksQuery";

interface PatchTaskVariables {
  id: string;
  patch: TaskPatch;
}

/**
 * Stable key so consumers can observe an in-flight task PATCH via
 * `useIsMutating`. The focus-refetch guard (`useTaskWriteInFlight`) gates on
 * this so a GET cannot clobber a just-committed terminal directive.
 */
export const PATCH_TASK_MUTATION_KEY = ["cc-db-api", "patchTask"];

/**
 * Partially updates a task row and replaces it in the cache.
 */
export default function usePatchTaskMutation() {
  const getAccessToken = useGetAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PATCH_TASK_MUTATION_KEY,
    mutationFn: ({ id, patch }: PatchTaskVariables) =>
      patchTask(id, patch, getAccessToken),
    // The terminal-status commit is fire-and-forget from the poller; a lost PATCH
    // leaves the server row non-terminal while the cache reads terminal, so the
    // card resurrects on reload. Retry transient failures (network / 5xx); a 4xx
    // is terminal and is not retried. Mirrors usePostTaskMutation.
    retry: (failureCount, error) => {
      const status = (error as { response?: Response })?.response?.status;
      if (status !== undefined && status >= 400 && status < 500) return false;
      return failureCount < 3;
    },
    onError: (error) => {
      console.error("Task patch failed:", error);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<Task[]>(TASKS_QUERY_KEY, (prev) =>
        prev?.map((j) => (j.id === data.id ? applyServerTaskRow(j, data) : j))
      );
    },
  });
}
